import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Stack,
  Tooltip,
  Fade,
  Tab,
  Tabs,
  Badge,
  TextField,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  AccessTime as AccessTimeIcon,
  Today as TodayIcon,
  DateRange as DateRangeIcon,
  CalendarMonth as CalendarMonthIcon,
  Security as SecurityIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  CheckCircle as ValidatedIcon,
  Cancel as RejectedIcon,
  Pending as PendingIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TimelineIcon,
  Map as MapIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  MonetizationOn as PriceIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  PlayArrow as ReactivateIcon,
  Dashboard as DashboardIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Star as StarIcon,
  ThumbUp as ThumbUpIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon2,
  ArrowForward as ArrowForwardIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Translate as TranslateIcon
} from '@mui/icons-material';
import { useNotifications } from '../../context/NotificationContext';
import api from '../../services/api';
import ReadOnlyMap from '../common/ReadOnlyMap';
import {
  DEACTIVATION_TYPES,
  DEACTIVATION_LABELS,
  getDeactivationReasons,
  getDeactivationLabel,
  isValidDeactivationType
} from '../../constants/deactivationTypes';

/**
 * Composant séparé pour l'accordéon des modifications
 * Extrait pour éviter les problèmes avec les hooks React
 */
const ChangesAccordion = ({ changes, formatFieldName, formatValue }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Filtrer les modifications pertinentes
  const relevantChanges = Object.entries(changes).filter(([field, change]) => {
    const isNumericKey = !isNaN(parseInt(field));
    const bothNA = 
      (change.from === 'N/A' || change.from === null || change.from === undefined) && 
      (change.to === 'N/A' || change.to === null || change.to === undefined);
    return !isNumericKey && !bothNA;
  });
  
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        onClick={() => setExpanded(!expanded)}
        color="primary"
        variant="text"
        size="small"
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ 
          textTransform: 'none', 
          fontWeight: 'medium',
          bgcolor: 'grey.100', 
          borderRadius: 1,
          '&:hover': { bgcolor: 'grey.200' }
        }}
        disabled={relevantChanges.length === 0}
      >
        {relevantChanges.length > 0 
          ? `Voir les détails des modifications (${relevantChanges.length})` 
          : "Aucune modification significative"}
      </Button>
      
      <Collapse in={expanded}>
        <Box sx={{ mt: 1, p: 2, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
            Détails des modifications:
          </Typography>
          
          {relevantChanges.length > 0 ? (
            relevantChanges.map(([field, change]) => (
              <Box key={field} sx={{ 
                mt: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <Typography variant="body2">
                  <strong>{formatFieldName(field)}:</strong> 
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center',
                  mt: 0.5
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      p: 1, 
                      bgcolor: 'error.light', 
                      color: 'error.dark',
                      borderRadius: 1,
                      fontWeight: 'medium',
                      flex: 1
                    }}
                  >
                    {formatValue(change.from)}
                  </Typography>
                  <ArrowForwardIcon color="action" fontSize="small" />
                  <Typography 
                    variant="body2"
                    sx={{ 
                      p: 1, 
                      bgcolor: 'success.light', 
                      color: 'success.dark',
                      borderRadius: 1,
                      fontWeight: 'medium',
                      flex: 1
                    }}
                  >
                    {formatValue(change.to)}
                  </Typography>
                </Box>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
              Aucune modification significative à afficher.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Composant de détail moderne et ergonomique d'une annonce pour l'administration
 */
const AdminListingDetail = ({ open, onClose, listingId, onRefresh }) => {
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  // États pour l'historique
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // États pour les modaux
  const [deactivateDialog, setDeactivateDialog] = useState({ open: false, listing: null });
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivateReasonText, setDeactivateReasonText] = useState(''); // Champ texte libre
  const [deactivationType, setDeactivationType] = useState(DEACTIVATION_TYPES.TEMPORARY); // Type de désactivation
  const [submitting, setSubmitting] = useState(false);

  // États pour le modal de réactivation
  const [reactivateDialog, setReactivateDialog] = useState({ open: false, listing: null });
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivateReasonText, setReactivateReasonText] = useState(''); // Champ texte libre

  // États pour les modals de validation et rejet
  const [validateDialog, setValidateDialog] = useState({ open: false, listing: null });
  const [validateReason, setValidateReason] = useState('');
  const [validateReasonText, setValidateReasonText] = useState(''); // Champ texte libre
  const [rejectDialog, setRejectDialog] = useState({ open: false, listing: null });
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonText, setRejectReasonText] = useState(''); // Champ texte libre

  // États pour la traduction
  const [translating, setTranslating] = useState(false);
  const [translatedReasons, setTranslatedReasons] = useState({
    deactivate: '',
    reactivate: '',
    reject: '',
    validate: ''
  });
  const [translationResults, setTranslationResults] = useState({
    deactivate: null,
    reactivate: null,
    reject: null,
    validate: null
  });
  const [showTranslations, setShowTranslations] = useState({
    deactivate: false,
    reactivate: false,
    reject: false,
    validate: false
  });

  const { showNotification } = useNotifications();

  // Utilisation des motifs centralisés pour la désactivation

  // Motifs prédéfinis pour la réactivation
  const predefinedReactivateReasons = [
    "Problème résolu - contenu mis à jour",
    "Images améliorées et conformes",
    "Description complétée et vérifiée",
    "Prix ajustés selon le marché",
    "Demande du propriétaire validée",
    "Maintenance terminée",
    "Problème de sécurité résolu",
    "Révision administrative favorable"
  ];

  // Motifs prédéfinis pour la validation
  const predefinedValidateReasons = [
    "Annonce conforme aux standards",
    "Contenu vérifié et approuvé",
    "Images de qualité satisfaisante",
    "Description complète et claire",
    "Prix conformes au marché",
    "Validation suite à corrections",
    "Approbation manuelle"
  ];

  // Motifs prédéfinis pour le rejet
  const predefinedRejectReasons = [
    "Images de mauvaise qualité ou manquantes",
    "Description insuffisante ou trompeuse",
    "Prix non conformes ou incohérents",
    "Contenu inapproprié ou non conforme",
    "Informations manquantes ou incorrectes",
    "Non-respect des conditions d'utilisation",
    "Problème de sécurité détecté"
  ];
  
  // Fonction pour formater les noms des champs
  const formatFieldName = (field) => {
    if (!field) return '';
    
    // Dictionnaire de traduction des noms de champs
    const fieldNames = {
      'title': 'Titre',
      'description': 'Description',
      'price': 'Prix',
      'status': 'Statut',
      'category': 'Catégorie',
      'address': 'Adresse',
      'city': 'Ville',
      'postal_code': 'Code postal',
      'images': 'Images',
      'created_at': 'Date de création',
      'updated_at': 'Date de modification'
    };
    
    return fieldNames[field] || field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
  };
  
  // Fonction pour formater les valeurs affichées
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (value === true) return 'Oui';
    if (value === false) return 'Non';
    
    // Essayer de détecter si c'est une date et la formater
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return new Date(value).toLocaleString('fr-FR');
    }
    
    return String(value);
  };

  // Charger les détails de l'annonce avec option pour forcer le rechargement depuis l'API
  const fetchListingDetails = async (id, forceRefresh = false) => {
    try {
      console.log('[AdminListingDetail] Fetching listing details for ID:', id, 'Force refresh:', forceRefresh);
      setLoading(true);
      
      // Ajouter un paramètre pour éviter la mise en cache par le navigateur ou l'API
      const timestamp = forceRefresh ? `?t=${Date.now()}` : '';
      const response = await api.get(`/listings/${id}${timestamp}`);
      const data = response.data;
      const listingData = data.listing || data;
      
      console.log('[AdminListingDetail] Raw response data:', data);
      console.log('[AdminListingDetail] Listing data status:', listingData.status);
      
      // Garantir que le statut est correctement défini
      if (!listingData.status) {
        console.warn('[AdminListingDetail] Status missing in response, using fallback');
      }
      
      setListing(listingData);
      setSelectedImage(0);
      
      // Log pour debug - confirmer le rechargement
      console.log('[AdminListingDetail] Listing details refreshed, new status:', listingData.status);
      
      return listingData; // Retourner les données pour utilisation ultérieure
      
    } catch (error) {
      console.error('[AdminListingDetail] Error fetching listing details:', error);
      showNotification('Erreur lors du chargement des détails de l\'annonce', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique de l'annonce
  const fetchListingHistory = async (id) => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/listings/${id}/history`);
      const historyData = response.data?.data?.history || [];
      setHistory(historyData);
      setHistoryLoaded(true);
    } catch (error) {
      console.error('[AdminListingDetail] Error fetching listing history:', error);
      showNotification('Erreur lors du chargement de l\'historique', 'error');
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open && listingId) {
      // Toujours forcer un rechargement frais à l'ouverture (true = bypass cache)
      console.log('[AdminListingDetail] Dialog opened, forcing fresh data load');
      fetchListingDetails(listingId, true);
    }
  }, [open, listingId]);

  // Fonctions de navigation pour les images
  const goToPreviousImage = () => {
    if (listing?.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === 0 ? listing.images.length - 1 : prev - 1);
    }
  };

  const goToNextImage = () => {
    if (listing?.images && listing.images.length > 1) {
      setSelectedImage(prev => prev === listing.images.length - 1 ? 0 : prev + 1);
    }
  };

  // Navigation au clavier pour les images
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (open && listing?.images && listing.images.length > 1) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          goToPreviousImage();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          goToNextImage();
        }
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, listing?.images, selectedImage, goToPreviousImage, goToNextImage]);

  // Fonction pour obtenir le chip de statut
  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: '#ff9800', bgColor: '#fff3e0' },
      active: { label: 'Active', color: '#4caf50', bgColor: '#e8f5e8' },
      rejected: { label: 'Rejetée', color: '#f44336', bgColor: '#ffebee' },
      inactive: { label: 'Inactive', color: '#9e9e9e', bgColor: '#f5f5f5' },
      draft: { label: 'Brouillon', color: '#9e9e9e', bgColor: '#f5f5f5' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 'bold',
          border: `1px solid ${config.color}`,
          px: 2,
          py: 1
        }}
      />
    );
  };

  // Fonction pour formater le prix
  const formatPrice = (price, currency = 'EUR') => {
    if (!price || isNaN(price)) return 'N/A';
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(price);
    } catch (error) {
      return `${price} ${currency}`;
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Fonction pour obtenir les prix disponibles
  const getAvailablePrices = () => {
    const prices = [];
    if (listing.price_hourly && listing.price_hourly > 0) {
      prices.push({ type: 'hourly', label: 'Horaire', value: listing.price_hourly, icon: '⏰', period: '/h' });
    }
    if (listing.price_daily && listing.price_daily > 0) {
      prices.push({ type: 'daily', label: 'Journalier', value: listing.price_daily, icon: '📅', period: '/jour' });
    }
    if (listing.price_weekly && listing.price_weekly > 0) {
      prices.push({ type: 'weekly', label: 'Hebdomadaire', value: listing.price_weekly, icon: '📊', period: '/sem' });
    }
    if (listing.price_monthly && listing.price_monthly > 0) {
      prices.push({ type: 'monthly', label: 'Mensuel', value: listing.price_monthly, icon: '📆', period: '/mois' });
    }
    if (prices.length === 0 && listing.price && listing.price > 0) {
      prices.push({ type: 'general', label: 'Prix', value: listing.price, icon: '💰', period: '' });
    }
    return prices;
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Charger l'historique si l'onglet historique est sélectionné (onglet 5)
    if (newValue === 5 && !historyLoaded && listingId) {
      fetchListingHistory(listingId);
    }
  };

  // Fonction pour ouvrir le modal de désactivation
  const openDeactivateDialog = (listing) => {
    setDeactivateDialog({ open: true, listing });
  };

  // Fonction pour fermer le modal de désactivation
  const closeDeactivateDialog = () => {
    setDeactivateDialog({ open: false, listing: null });
    setDeactivateReason('');
    setDeactivateReasonText(''); // Réinitialiser le champ texte libre
    setDeactivationType(DEACTIVATION_TYPES.TEMPORARY); // Réinitialiser le type de désactivation
    // Réinitialiser les états de traduction pour la désactivation
    setTranslatedReasons(prev => ({ ...prev, deactivate: '' }));
    setTranslationResults(prev => ({ ...prev, deactivate: null }));
    setShowTranslations(prev => ({ ...prev, deactivate: false }));
  };

  // Fonction pour ouvrir le modal de réactivation
  const openReactivateDialog = (listing) => {
    setReactivateDialog({ open: true, listing });
  };

  // Fonction pour fermer le modal de réactivation
  const closeReactivateDialog = () => {
    setReactivateDialog({ open: false, listing: null });
    setReactivateReason('');
    setReactivateReasonText(''); // Réinitialiser le champ texte libre
    // Réinitialiser les états de traduction pour la réactivation
    setTranslatedReasons(prev => ({ ...prev, reactivate: '' }));
    setTranslationResults(prev => ({ ...prev, reactivate: null }));
    setShowTranslations(prev => ({ ...prev, reactivate: false }));
  };

  // Fonction générique pour traduire un motif
  const handleTranslateReason = async (type, reason, listingId) => {
    if (!reason || !reason.trim()) {
      showNotification('Veuillez saisir un motif avant de traduire', 'warning');
      return;
    }

    if (reason.trim().length < 10) {
      showNotification('Le motif doit contenir au moins 10 caractères', 'warning');
      return;
    }

    setTranslating(true);
    try {
      console.log(`[AdminListingDetail] Translating ${type} reason for listing:`, listingId);
      const response = await api.post(`/admin/listings/${listingId}/translate-rejection`, {
        reason: reason.trim()
      });

      if (response.data.success) {
        const translation = response.data.translation;
        setTranslationResults(prev => ({ ...prev, [type]: translation }));

        if (translation.needsTranslation) {
          if (translation.translatedReason) {
            setTranslatedReasons(prev => ({ ...prev, [type]: translation.translatedReason }));
            setShowTranslations(prev => ({ ...prev, [type]: true }));
            showNotification(`Traduction générée en ${translation.targetLanguage}`, 'success');
          } else {
            showNotification(response.data.message || 'Erreur lors de la traduction', 'error');
          }
        } else {
          showNotification(response.data.message, 'info');
        }
      } else {
        showNotification(response.data.message || 'Erreur lors de la traduction', 'error');
      }
    } catch (error) {
      console.error(`[AdminListingDetail] Error translating ${type} reason:`, error);
      if (error.response?.status === 503) {
        showNotification('Service de traduction indisponible', 'error');
      } else if (error.response?.status === 456) {
        showNotification('Quota de traduction épuisé. Veuillez traduire manuellement.', 'warning');
      } else {
        showNotification('Erreur lors de la traduction', 'error');
      }
    } finally {
      setTranslating(false);
    }
  };

  // Fonctions pour les modals de validation et rejet
  const closeValidateDialog = () => {
    setValidateDialog({ open: false, listing: null });
    setValidateReason('');
    setValidateReasonText(''); // Réinitialiser le champ texte libre
    // Réinitialiser les états de traduction pour la validation
    setTranslatedReasons(prev => ({ ...prev, validate: '' }));
    setTranslationResults(prev => ({ ...prev, validate: null }));
    setShowTranslations(prev => ({ ...prev, validate: false }));
  };

  const closeRejectDialog = () => {
    setRejectDialog({ open: false, listing: null });
    setRejectReason('');
    setRejectReasonText(''); // Réinitialiser le champ texte libre
    // Réinitialiser les états de traduction pour le rejet
    setTranslatedReasons(prev => ({ ...prev, reject: '' }));
    setTranslationResults(prev => ({ ...prev, reject: null }));
    setShowTranslations(prev => ({ ...prev, reject: false }));
  };

  const handleValidateSubmit = async () => {
    try {
      setSubmitting(true);
      
      const listingId = validateDialog.listing.id;
      console.log('[AdminListingDetail] Starting validation for listing ID:', listingId);
      
      // Utiliser directement le contenu du champ texte libre
      const reasonToUse = validateReasonText.trim();
      
      await api.post(`/admin/listings/${listingId}/validate`, {
        notes: reasonToUse || 'Validé par un administrateur'
      });
      
      showNotification('Annonce validée avec succès', 'success');
      
      // Mettre à jour l'état local
      setListing(prevListing => ({ ...prevListing, status: 'active' }));
      
      closeValidateDialog();
      
      // Rafraîchir les données
      setTimeout(async () => {
        await fetchListingDetails(listingId, true);
        if (onRefresh) onRefresh();
      }, 100);
      
    } catch (error) {
      console.error('[AdminListingDetail] Error validating listing:', error);
      showNotification('Erreur lors de la validation', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReasonText.trim()) {
      showNotification('Veuillez spécifier une raison pour le rejet.', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      
      const listingId = rejectDialog.listing.id;
      console.log('[AdminListingDetail] Starting rejection for listing ID:', listingId);
      
      // Utiliser directement le contenu du champ texte libre
      const reasonToUse = rejectReasonText.trim();
      
      await api.post(`/admin/listings/${listingId}/reject`, {
        reason: reasonToUse
      });
      
      showNotification('Annonce rejetée avec succès', 'success');
      
      // Mettre à jour l'état local
      setListing(prevListing => ({ ...prevListing, status: 'rejected' }));
      
      closeRejectDialog();
      
      // Rafraîchir les données
      setTimeout(async () => {
        await fetchListingDetails(listingId, true);
        if (onRefresh) onRefresh();
      }, 100);
      
    } catch (error) {
      console.error('[AdminListingDetail] Error rejecting listing:', error);
      showNotification('Erreur lors du rejet', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour soumettre le formulaire de désactivation
  const handleDeactivateSubmit = async () => {
    if (!deactivateReasonText.trim()) {
      showNotification('Veuillez spécifier une raison pour la désactivation.', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      
      // Sauvegarder l'ID avant les opérations
      const listingId = deactivateDialog.listing.id;
      console.log('[AdminListingDetail] Starting deactivation for listing ID:', listingId);
      
      // Utiliser directement le contenu du champ texte libre
      const reasonToUse = deactivateReasonText.trim();
      
      // Effectuer la désactivation
      await api.put(`/admin/listings/${listingId}/status`, {
        status: 'inactive',
        reason: reasonToUse,
        deactivationType: deactivationType // Envoyer le type de désactivation
      });
      
      console.log('[AdminListingDetail] Deactivation API call successful');
      showNotification('Annonce désactivée avec succès', 'success');
      
      // Important: mettre à jour immédiatement l'état local pour une interface cohérente
      setListing(prevListing => {
        const updatedListing = { ...prevListing, status: 'inactive' };
        console.log('[AdminListingDetail] Updated local state to inactive:', updatedListing);
        return updatedListing;
      });
      
      // Fermer le modal de désactivation
      closeDeactivateDialog();
      console.log('[AdminListingDetail] Modal closed');
      
      // Forcer un rechargement complet depuis l'API pour s'assurer que tout est cohérent
      setTimeout(async () => {
        const freshData = await fetchListingDetails(listingId, true); // true = forcer le rechargement
        console.log('[AdminListingDetail] Fresh data after deactivation:', freshData?.status);
        
        // Rafraîchir la liste parent si nécessaire
        if (onRefresh) {
          console.log('[AdminListingDetail] Calling parent refresh...');
          onRefresh();
        }
      }, 100);
      
    } catch (error) {
      console.error('[AdminListingDetail] Error deactivating listing:', error);
      showNotification('Erreur lors de la désactivation de l\'annonce', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour soumettre le formulaire de réactivation
  const handleReactivateSubmit = async () => {
    if (!reactivateReasonText.trim()) {
      showNotification('Veuillez spécifier une raison pour la réactivation.', 'warning');
      return;
    }

    try {
      setSubmitting(true);
      
      // Sauvegarder l'ID avant les opérations
      const listingId = reactivateDialog.listing.id;
      console.log('[AdminListingDetail] Starting reactivation for listing ID:', listingId);
      
      // Utiliser directement le contenu du champ texte libre
      const reasonToUse = reactivateReasonText.trim();
      
      // Effectuer la réactivation
      await api.put(`/admin/listings/${listingId}/status`, {
        status: 'active',
        reason: reasonToUse
      });
      
      console.log('[AdminListingDetail] Reactivation API call successful');
      showNotification('Annonce réactivée avec succès', 'success');
      
      // Important: mettre à jour immédiatement l'état local pour une interface cohérente
      setListing(prevListing => {
        const updatedListing = { ...prevListing, status: 'active' };
        console.log('[AdminListingDetail] Updated local state to active:', updatedListing);
        return updatedListing;
      });
      
      // Fermer le modal de réactivation
      closeReactivateDialog();
      console.log('[AdminListingDetail] Reactivation modal closed');
      
      // Forcer un rechargement complet depuis l'API pour s'assurer que tout est cohérent
      setTimeout(async () => {
        const freshData = await fetchListingDetails(listingId, true); // true = forcer le rechargement
        console.log('[AdminListingDetail] Fresh data after reactivation:', freshData?.status);
        
        // Rafraîchir la liste parent si nécessaire
        if (onRefresh) {
          console.log('[AdminListingDetail] Calling parent refresh after reactivation...');
          onRefresh();
        }
      }, 100);
      
    } catch (error) {
      console.error('[AdminListingDetail] Error reactivating listing:', error);
      showNotification('Erreur lors de la réactivation de l\'annonce', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ py: 8 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="h6" color="textSecondary">
              Chargement des détails de l'annonce...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!listing) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>Erreur de chargement</Typography>
            <Typography>Impossible de charger les détails de l'annonce</Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">Fermer</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const availablePrices = getAvailablePrices();

  // Composant pour l'onglet Aperçu
  const OverviewTab = () => (
    <Box>
      {/* En-tête avec informations principales */}
      <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* Colonne gauche - Informations principales */}
            <Grid item xs={12} md={8}>
              <Box mb={3}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {listing.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  {getStatusChip(listing.status)}
                  <Chip
                    icon={<VisibilityIcon />}
                    label={`${listing.views || 0} vues`}
                    variant="outlined"
                    color="primary"
                  />
                </Box>
              </Box>

              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'medium' }}>
                Description
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                {listing.description || 'Aucune description disponible'}
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap" mt={3}>
                <Chip
                  icon={<CategoryIcon />}
                  label={listing.categoryName || listing.category || 'Non définie'}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<LocationIcon />}
                  label={listing.city || 'Non définie'}
                  color="secondary"
                  variant="outlined"
                />
                <Chip
                  icon={<CalendarIcon />}
                  label={formatDate(listing.createdAt)}
                  variant="outlined"
                />
              </Box>
            </Grid>

            {/* Colonne droite - Tarification */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  💰 Tarification
                </Typography>
                {availablePrices.length > 0 ? (
                  <Stack spacing={2}>
                    {availablePrices.map((priceInfo) => (
                      <Box
                        key={priceInfo.type}
                        sx={{
                          p: 2,
                          backgroundColor: 'white',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ fontSize: '1.2rem' }}>{priceInfo.icon}</Typography>
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {priceInfo.label}
                            </Typography>
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                              {formatPrice(priceInfo.value, listing.currency)}{priceInfo.period}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="warning">Aucun prix configuré</Alert>
                )}
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  // Composant pour l'onglet Images
  const ImagesTab = () => (
    <Box>
      {listing.images && listing.images.length > 0 ? (
        <Grid container spacing={3}>
          {/* Image principale */}
          <Grid item xs={12} md={8}>
            <Card sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
              <CardMedia
                component="img"
                height="500"
                image={listing.images[selectedImage]?.url || listing.images[selectedImage]?.image_url || '/placeholder.jpg'}
                alt={`Image ${selectedImage + 1}`}
                sx={{ objectFit: 'cover' }}
              />
              
              {listing.images.length > 1 && (
                <>
                  <IconButton
                    onClick={goToPreviousImage}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                      width: 56,
                      height: 56
                    }}
                  >
                    <ChevronLeftIcon fontSize="large" />
                  </IconButton>
                  
                  <IconButton
                    onClick={goToNextImage}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                      width: 56,
                      height: 56
                    }}
                  >
                    <ChevronRightIcon fontSize="large" />
                  </IconButton>
                  
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 16,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      fontWeight: 'medium'
                    }}
                  >
                    {selectedImage + 1} / {listing.images.length}
                  </Box>
                </>
              )}
            </Card>
          </Grid>

          {/* Miniatures */}
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
              Toutes les images ({listing.images.length})
            </Typography>
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
              <Grid container spacing={1}>
                {listing.images.map((image, index) => (
                  <Grid item xs={6} key={index}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: index === selectedImage ? '3px solid' : '1px solid',
                        borderColor: index === selectedImage ? 'primary.main' : 'grey.300',
                        borderRadius: 1,
                        overflow: 'hidden',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 2
                        }
                      }}
                      onClick={() => setSelectedImage(index)}
                    >
                      <CardMedia
                        component="img"
                        height="100"
                        image={image.url || image.image_url || '/placeholder.jpg'}
                        alt={`Miniature ${index + 1}`}
                        sx={{ objectFit: 'cover' }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info" sx={{ textAlign: 'center', py: 4 }}>
          <PhotoLibraryIcon sx={{ fontSize: 48, mb: 2, color: 'info.main' }} />
          <Typography variant="h6" gutterBottom>Aucune image disponible</Typography>
          <Typography>Cette annonce ne contient aucune image</Typography>
        </Alert>
      )}
    </Box>
  );

  // Composant pour l'onglet Localisation
  const LocationTab = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            📍 Informations d'adresse
          </Typography>
          <List>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LocationIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Adresse"
                secondary={listing.address || 'Non spécifiée'}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <BusinessIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Ville"
                secondary={listing.city || 'Non spécifiée'}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Code postal"
                secondary={listing.postalCode || listing.postal_code || 'Non spécifié'}
                primaryTypographyProps={{ fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontFamily: 'monospace' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MapIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Pays"
                secondary={listing.country || 'Non spécifié'}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            {(listing.latitude && listing.longitude) && (
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LocationIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Coordonnées GPS"
                  secondary={`${listing.latitude}, ${listing.longitude}`}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                  secondaryTypographyProps={{ fontFamily: 'monospace' }}
                />
              </ListItem>
            )}
          </List>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        {listing.latitude && listing.longitude ? (
          <Card sx={{ height: 400, overflow: 'hidden', borderRadius: 2 }}>
            <ReadOnlyMap
              latitude={parseFloat(listing.latitude)}
              longitude={parseFloat(listing.longitude)}
              title={listing.title}
              height={400}
              zoom={16}
              showAddress={false}
            />
          </Card>
        ) : (
          <Card sx={{ p: 3, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Alert severity="info">
              <Typography variant="h6" gutterBottom>Aucune coordonnée GPS</Typography>
              <Typography>Impossible d'afficher la carte pour cette annonce</Typography>
            </Alert>
          </Card>
        )}
      </Grid>
    </Grid>
  );

  // Composant pour l'onglet Propriétaire
  const OwnerTab = () => (
    <Card sx={{ p: 4 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        👤 Informations du propriétaire
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" mb={3}>
            <Avatar
              sx={{
                mr: 3,
                width: 80,
                height: 80,
                backgroundColor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}
            >
              {listing.user?.name?.[0] || listing.owner?.name?.[0] || '?'}
            </Avatar>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium' }}>
                {listing.user?.name || listing.owner?.name || 'Utilisateur supprimé'}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {listing.user?.email || listing.owner?.email || 'Email non disponible'}
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <PersonIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="ID Utilisateur"
                secondary={listing.user?.id || listing.owner?.id || listing.userId || 'N/A'}
                primaryTypographyProps={{ fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontFamily: 'monospace' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <CalendarIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Membre depuis"
                secondary={formatDate(listing.user?.createdAt || listing.owner?.createdAt)}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            {(listing.user?.phone || listing.owner?.phone) && (
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <PhoneIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Téléphone"
                  secondary={listing.user?.phone || listing.owner?.phone}
                  primaryTypographyProps={{ fontWeight: 'medium' }}
                />
              </ListItem>
            )}
          </List>
        </Grid>
      </Grid>
    </Card>
  );

  // Composant pour l'onglet Historique
  const HistoryTab = () => {
    const getActionIcon = (action) => {
      switch (action) {
        case 'created': return <CheckIcon color="success" />;
        case 'updated': return <SettingsIcon color="primary" />;
        case 'validated': return <CheckIcon color="success" />;
        case 'rejected': return <ErrorIcon color="error" />;
        case 'deactivated': return <WarningIcon color="warning" />;
        case 'reactivated': return <ReactivateIcon color="success" />;
        case 'status_changed': return <SettingsIcon color="info" />;
        default: return <HistoryIcon color="action" />;
      }
    };

    const getActionLabel = (action) => {
      const labels = {
        'created': 'Création',
        'updated': 'Modification',
        'validated': 'Validation',
        'rejected': 'Rejet',
        'deactivated': 'Désactivation',
        'reactivated': 'Réactivation',
        'status_changed': 'Changement de statut',
        'deleted': 'Suppression'
      };
      return labels[action] || action;
    };

    const getActionColor = (action) => {
      switch (action) {
        case 'created': return 'success.main';
        case 'validated': return 'success.main';
        case 'reactivated': return 'success.main';
        case 'rejected': return 'error.main';
        case 'deactivated': return 'warning.main';
        case 'updated': return 'primary.main';
        case 'status_changed': return 'info.main';
        default: return 'text.secondary';
      }
    };

    if (historyLoading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" py={6}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Chargement de l'historique...
          </Typography>
        </Box>
      );
    }

    if (history.length === 0) {
      return (
        <Alert severity="info" sx={{ textAlign: 'center', py: 4 }}>
          <HistoryIcon sx={{ fontSize: 48, mb: 2, color: 'info.main' }} />
          <Typography variant="h6" gutterBottom>Aucun historique disponible</Typography>
          <Typography>Cette annonce n'a pas encore d'historique d'actions</Typography>
        </Alert>
      );
    }

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
          📜 Historique des actions ({history.length})
        </Typography>
        
        <List sx={{ width: '100%' }}>
          {history.map((entry, index) => (
            <React.Fragment key={entry.id || index}>
              <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                <ListItemIcon sx={{ mt: 1 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: getActionColor(entry.action) }}>
                    {getActionIcon(entry.action)}
                  </Avatar>
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {getActionLabel(entry.action)}
                      </Typography>
                      <Chip 
                        label={entry.action} 
                        size="small" 
                        sx={{ 
                          bgcolor: getActionColor(entry.action),
                          color: 'white',
                          fontSize: '0.75rem'
                        }} 
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <TimeIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="textSecondary">
                          {formatDate(entry.timestamp)}
                        </Typography>
                      </Box>
                      
                      {entry.performer && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <PersonIcon2 fontSize="small" color="action" />
                          <Typography variant="body2" color="textSecondary">
                            Par: {entry.performer.name} ({entry.performed_by_role})
                          </Typography>
                        </Box>
                      )}
                      
                      {(entry.previous_status || entry.new_status) && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Typography variant="body2" color="textSecondary">
                            Statut: {entry.previous_status || 'N/A'} → {entry.new_status || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      
                      {entry.comment && (
                        <Box sx={{ 
                          mt: 1, 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 1,
                          borderLeft: '4px solid',
                          borderLeftColor: getActionColor(entry.action)
                        }}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            "{entry.comment}"
                          </Typography>
                        </Box>
                      )}
                      
                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <ChangesAccordion 
                          changes={entry.changes} 
                          formatFieldName={formatFieldName}
                          formatValue={formatValue}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItem>
              
              {index < history.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Box>
    );
  };

  // Composant pour l'onglet Administration
  const AdminTab = () => (
    <Grid container spacing={3}>
      {/* Informations techniques */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'info.main' }}>
            🔧 Informations techniques
          </Typography>
          <List>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <InfoIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="ID Annonce"
                secondary={listing.id}
                primaryTypographyProps={{ fontWeight: 'medium' }}
                secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <CalendarIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Date de création"
                secondary={formatDate(listing.createdAt)}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <ScheduleIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Dernière modification"
                secondary={formatDate(listing.updatedAt)}
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon>
                <VisibilityIcon color="info" />
              </ListItemIcon>
              <ListItemText
                primary="Nombre de vues"
                secondary={
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {listing.views || 0}
                  </Typography>
                }
                primaryTypographyProps={{ fontWeight: 'medium' }}
              />
            </ListItem>
          </List>
        </Card>
      </Grid>

      {/* Historique de validation */}
      <Grid item xs={12} md={6}>
        <Card sx={{ p: 3, height: '100%' }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
            🛡️ Historique de validation
          </Typography>
          
          {listing.validatedAt ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                ✅ Annonce validée
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(listing.validatedAt)}
              </Typography>
              {listing.validatedBy && (
                <Typography variant="body2">
                  <strong>Par:</strong> {listing.validatedBy.name || listing.validatedBy}
                </Typography>
              )}
            </Alert>
          ) : listing.rejectedAt ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                ❌ Annonce rejetée
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {formatDate(listing.rejectedAt)}
              </Typography>
              {listing.rejectedBy && (
                <Typography variant="body2" gutterBottom>
                  <strong>Par:</strong> {listing.rejectedBy.name || listing.rejectedBy}
                </Typography>
              )}
              {listing.rejectionReason && (
                <Typography variant="body2">
                  <strong>Raison:</strong> {listing.rejectionReason}
                </Typography>
              )}
            </Alert>
          ) : (
            <Alert severity="warning">
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                ⏳ En attente de validation
              </Typography>
              <Typography variant="body2">
                Cette annonce n'a pas encore été validée par un administrateur.
              </Typography>
            </Alert>
          )}
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { 
          height: '95vh',
          borderRadius: 2
        }
      }}
    >
      {/* Résumé sticky et actions rapides */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1201,
        background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        px: 4,
        py: 2,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        boxShadow: '0 4px 24px 0 rgba(102,126,234,0.10)',
        mb: 1
      }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} md={7}>
            <Box display="flex" alignItems="center" gap={2}>
              <AdminIcon fontSize="large" />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                  {listing.title}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {getStatusChip(listing.status)}
                  <Chip
                    icon={<VisibilityIcon />}
                    label={`${listing.views || 0} vues`}
                    variant="filled"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }}
                  />
                  <Chip
                    icon={<PriceIcon />}
                    label={formatPrice(listing.price_daily || listing.price || 0, listing.currency)}
                    variant="filled"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }}
                  />
                  <Chip
                    icon={<CalendarIcon />}
                    label={formatDate(listing.createdAt)}
                    variant="filled"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 'bold' }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={2}>
              {/* Actions rapides (exemple, à adapter selon droits et statut) */}
              <Tooltip title="Valider l'annonce" disableHoverListener={loading} disableFocusListener={loading} disableInteractive={loading}>
                <span>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<ValidatedIcon />}
                    sx={{ fontWeight: 'bold', px: 3, boxShadow: 2 }}
                    onClick={() => setValidateDialog({ open: true, listing })}
                    disabled={listing.status !== 'pending' || loading}
                  >
                    Valider
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Rejeter l'annonce" disableHoverListener={loading} disableFocusListener={loading} disableInteractive={loading}>
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<RejectedIcon />}
                    sx={{ fontWeight: 'bold', px: 3, boxShadow: 2 }}
                    onClick={async () => {
                      if (listing.status !== 'pending') {
                        showNotification('Impossible de rejeter une annonce qui n’est pas en attente de validation.', 'info');
                        return;
                      }
                      setRejectDialog({ open: true, listing });
                    }}
                    disabled={listing.status !== 'pending' || loading}
                  >
                    Rejeter
                  </Button>
                </span>
              </Tooltip>
              {/* Bouton de désactivation - uniquement visible si l'annonce n'est PAS inactive */}
              {listing.status !== 'inactive' && (
                <Tooltip title="Désactiver l'annonce" disableHoverListener={loading || submitting} disableFocusListener={loading || submitting} disableInteractive={loading || submitting}>
                  <span>
                    <Button
                      variant="contained"
                      color="warning"
                      size="large"
                      startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <PendingIcon />}
                      sx={{ fontWeight: 'bold', px: 3, boxShadow: 2 }}
                      onClick={() => {
                        console.log('[AdminListingDetail] Deactivate button clicked, current status:', listing.status);
                        if (listing.status === 'inactive') {
                          showNotification('Annonce déjà désactivée.', 'info');
                          return;
                        }
                        openDeactivateDialog(listing);
                      }}
                      disabled={loading || submitting}
                    >
                      {submitting ? 'Désactivation...' : 'Désactiver'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              
              {/* Bouton de réactivation - affiché uniquement si l'annonce est inactive */}
              {listing.status === 'inactive' && (
                <Tooltip title="Réactiver l'annonce" disableHoverListener={loading || submitting} disableFocusListener={loading || submitting} disableInteractive={loading || submitting}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ReactivateIcon />}
                      sx={{ fontWeight: 'bold', px: 3, boxShadow: 2 }}
                      onClick={() => {
                        console.log('[AdminListingDetail] Reactivate button clicked, current status:', listing.status);
                        openReactivateDialog(listing);
                      }}
                      disabled={loading || submitting}
                    >
                      {submitting ? 'Réactivation...' : 'Réactiver'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <IconButton
                onClick={onClose}
                sx={{ 
                  color: 'white',
                  ml: 2,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Navigation par onglets */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: '#f8f9fa' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 3 }}
          >
            <Tab 
              label="Aperçu" 
              icon={<DescriptionIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
            <Tab 
              label={
                <Badge badgeContent={listing.images?.length || 0} color="primary">
                  Images
                </Badge>
              } 
              icon={<PhotoLibraryIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
            <Tab 
              label="Localisation" 
              icon={<LocationIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
            <Tab 
              label="Propriétaire" 
              icon={<PersonIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
            <Tab 
              label="Administration" 
              icon={<SecurityIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
            <Tab 
              label={
                <Badge badgeContent={history.length || 0} color="secondary">
                  Historique
                </Badge>
              } 
              icon={<HistoryIcon />} 
              iconPosition="start"
              sx={{ minHeight: 64, fontWeight: 'medium' }}
            />
          </Tabs>
        </Box>

        {/* Contenu des onglets */}
        <Box sx={{ p: 3, minHeight: 500 }}>
          {activeTab === 0 && <OverviewTab />}
          {activeTab === 1 && <ImagesTab />}
          {activeTab === 2 && <LocationTab />}
          {activeTab === 3 && <OwnerTab />}
          {activeTab === 4 && <AdminTab />}
          {activeTab === 5 && <HistoryTab />}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          startIcon={<CloseIcon />}
          sx={{ px: 4 }}
        >
          Fermer
        </Button>
        {onRefresh && (
          <Button
            onClick={() => { onRefresh(); onClose(); }}
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            sx={{ px: 4 }}
          >
            Actualiser la liste
          </Button>
        )}
      </DialogActions>

      {/* Dialog pour la désactivation */}
      <Dialog
        open={deactivateDialog.open}
        onClose={closeDeactivateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Désactiver l'annonce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Êtes-vous sûr de vouloir désactiver cette annonce ?
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          {/* Sélection du type de désactivation */}
          <Typography variant="subtitle2" gutterBottom>
            Type de désactivation :
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            value={deactivationType}
            onChange={(e) => setDeactivationType(e.target.value)}
            sx={{ mb: 2 }}
            helperText={
              deactivationType === DEACTIVATION_TYPES.PERMANENT 
                ? '⚠️ ATTENTION : L\'utilisateur ne pourra plus modifier cette annonce'
                : 'ℹ️ L\'utilisateur pourra corriger et resoumettre l\'annonce'
            }
            disabled={submitting || translating}
          >
            <MenuItem value={DEACTIVATION_TYPES.TEMPORARY}>
              🔄 Désactivation temporaire (permet la resoumission)
            </MenuItem>
            <MenuItem value={DEACTIVATION_TYPES.PERMANENT}>
              🚫 Désactivation permanente (bloque les modifications)
            </MenuItem>
          </TextField>
          
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Raison de la désactivation (facultatif) :
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            value={deactivateReason}
            onChange={(e) => {
              setDeactivateReason(e.target.value);
              // Pré-remplir le champ texte libre avec la valeur sélectionnée
              setDeactivateReasonText(e.target.value);
              // Réinitialiser la traduction si le motif change
              setTranslatedReasons(prev => ({ ...prev, deactivate: '' }));
              setTranslationResults(prev => ({ ...prev, deactivate: null }));
              setShowTranslations(prev => ({ ...prev, deactivate: false }));
            }}
            sx={{ mb: 2 }}
            helperText="Sélectionnez un motif prédéfini ou saisissez-en un personnalisé ci-dessous"
            disabled={submitting || translating}
          >
            <MenuItem value="">
              <em>Choisir une raison prédéfinie</em>
            </MenuItem>
            {getDeactivationReasons(deactivationType).map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>

          {/* Champ texte libre pour le motif */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Motif de la désactivation :
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={deactivateReasonText}
            onChange={(e) => {
              setDeactivateReasonText(e.target.value);
              // Réinitialiser la traduction si le texte change
              setTranslatedReasons(prev => ({ ...prev, deactivate: '' }));
              setTranslationResults(prev => ({ ...prev, deactivate: null }));
              setShowTranslations(prev => ({ ...prev, deactivate: false }));
            }}
            placeholder="Saisissez ou modifiez le motif de désactivation..."
            sx={{ mb: 2 }}
            helperText={
              deactivateReasonText.trim().length > 0 && deactivateReasonText.trim().length < 10
                ? 'Le motif doit contenir au moins 10 caractères'
                : 'Ce motif sera envoyé au propriétaire de l\'annonce et peut être traduit'
            }
            disabled={submitting || translating}
          />

          {/* Bouton de traduction */}
          <Box display="flex" alignItems="center" gap={2} mt={1} mb={2}>
            <Button
              variant="outlined"
              startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
              onClick={() => handleTranslateReason('deactivate', deactivateReasonText, deactivateDialog.listing?.id)}
              disabled={!deactivateReasonText.trim() || deactivateReasonText.trim().length < 10 || submitting || translating}
              size="small"
            >
              {translating ? 'Traduction...' : 'Traduire automatiquement'}
            </Button>
            {translationResults.deactivate && (
              <Chip 
                label={`Vers: ${translationResults.deactivate.targetLanguage}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Affichage de la traduction proposée */}
          {showTranslations.deactivate && translatedReasons.deactivate && (
            <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Traduction proposée en {translationResults.deactivate?.targetLanguage} :
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                "{translatedReasons.deactivate}"
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem', flex: 1, mr: 2 }}>
                  Vérifiez la traduction et cliquez sur "Utiliser cette traduction" pour l'insérer dans le champ motif.
                </Alert>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => {
                    setDeactivateReasonText(translatedReasons.deactivate);
                    showNotification('Traduction utilisée dans le champ motif', 'success');
                  }}
                >
                  Utiliser cette traduction
                </Button>
              </Box>
            </Box>
          )}

          {/* Notes de traduction en cas d'erreur */}
          {translationResults.deactivate && translationResults.deactivate.error && (
            <Box mb={2}>
              <Alert severity="warning">
                {translationResults.deactivate.error === 'Quota épuisé' 
                  ? 'Quota de traduction épuisé. Veuillez traduire manuellement le motif dans la langue du propriétaire.' 
                  : 'Erreur de traduction. Veuillez vérifier que votre motif est en français et réessayer.'}
              </Alert>
            </Box>
          )}

          {/* Alerte pour désactivation permanente */}
          {deactivationType === DEACTIVATION_TYPES.PERMANENT && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>⚠️ ATTENTION - Désactivation Permanente :</strong><br/>
                • L'utilisateur ne pourra plus modifier cette annonce<br/>
                • Cette action doit être réservée aux cas graves (fraude, contenu illégal, etc.)<br/>
                • Assurez-vous que cette décision est justifiée et définitive
              </Typography>
            </Alert>
          )}

          <Typography variant="caption" color="textSecondary">
            Vous pouvez également saisir une raison personnalisée dans le champ ci-dessus.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivateDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleDeactivateSubmit}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (
              showTranslations.deactivate ? 'Confirmer la désactivation avec traduction' : 'Confirmer la désactivation'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour la réactivation */}
      <Dialog
        open={reactivateDialog.open}
        onClose={closeReactivateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReactivateIcon />
          Réactiver l'annonce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Êtes-vous sûr de vouloir réactiver cette annonce ? Elle redeviendra visible et accessible aux utilisateurs.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Raison de la réactivation (recommandé) :
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            value={reactivateReason}
            onChange={(e) => {
              setReactivateReason(e.target.value);
              // Pré-remplir le champ texte libre avec la valeur sélectionnée
              setReactivateReasonText(e.target.value);
              // Réinitialiser la traduction si le motif change
              setTranslatedReasons(prev => ({ ...prev, reactivate: '' }));
              setTranslationResults(prev => ({ ...prev, reactivate: null }));
              setShowTranslations(prev => ({ ...prev, reactivate: false }));
            }}
            sx={{ mb: 2 }}
            helperText="Sélectionnez un motif prédéfini ou saisissez-en un personnalisé ci-dessous"
            disabled={submitting || translating}
          >
            <MenuItem value="">
              <em>Choisir une raison prédéfinie</em>
            </MenuItem>
            {predefinedReactivateReasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>

          {/* Champ texte libre pour le motif */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Motif de la réactivation :
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={reactivateReasonText}
            onChange={(e) => {
              setReactivateReasonText(e.target.value);
              // Réinitialiser la traduction si le texte change
              setTranslatedReasons(prev => ({ ...prev, reactivate: '' }));
              setTranslationResults(prev => ({ ...prev, reactivate: null }));
              setShowTranslations(prev => ({ ...prev, reactivate: false }));
            }}
            placeholder="Saisissez ou modifiez le motif de réactivation..."
            sx={{ mb: 2 }}
            helperText={
              reactivateReasonText.trim().length > 0 && reactivateReasonText.trim().length < 10
                ? 'Le motif doit contenir au moins 10 caractères'
                : 'Ce motif sera envoyé au propriétaire de l\'annonce et peut être traduit'
            }
            disabled={submitting || translating}
          />

          {/* Bouton de traduction */}
          <Box display="flex" alignItems="center" gap={2} mt={1} mb={2}>
            <Button
              variant="outlined"
              startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
              onClick={() => handleTranslateReason('reactivate', reactivateReasonText, reactivateDialog.listing?.id)}
              disabled={!reactivateReasonText.trim() || reactivateReasonText.trim().length < 10 || submitting || translating}
              size="small"
            >
              {translating ? 'Traduction...' : 'Traduire automatiquement'}
            </Button>
            {translationResults.reactivate && (
              <Chip 
                label={`Vers: ${translationResults.reactivate.targetLanguage}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Affichage de la traduction proposée */}
          {showTranslations.reactivate && translatedReasons.reactivate && (
            <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Traduction proposée en {translationResults.reactivate?.targetLanguage} :
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                "{translatedReasons.reactivate}"
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem', flex: 1, mr: 2 }}>
                  Vérifiez la traduction et cliquez sur "Utiliser cette traduction" pour l'insérer dans le champ motif.
                </Alert>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => {
                    setReactivateReasonText(translatedReasons.reactivate);
                    showNotification('Traduction utilisée dans le champ motif', 'success');
                  }}
                >
                  Utiliser cette traduction
                </Button>
              </Box>
            </Box>
          )}

          {/* Notes de traduction en cas d'erreur */}
          {translationResults.reactivate && translationResults.reactivate.error && (
            <Box mb={2}>
              <Alert severity="warning">
                {translationResults.reactivate.error === 'Quota épuisé' 
                  ? 'Quota de traduction épuisé. Veuillez traduire manuellement le motif dans la langue du propriétaire.' 
                  : 'Erreur de traduction. Veuillez vérifier que votre motif est en français et réessayer.'}
              </Alert>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note :</strong> La réactivation rendra l'annonce immédiatement visible aux utilisateurs. 
              Assurez-vous que tous les problèmes qui avaient mené à sa désactivation ont été résolus.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReactivateDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleReactivateSubmit}
            color="success"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <ReactivateIcon />}
          >
            {submitting ? 'Réactivation...' : (
              showTranslations.reactivate ? 'Confirmer la réactivation avec traduction' : 'Confirmer la réactivation'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour la validation */}
      <Dialog
        open={validateDialog.open}
        onClose={closeValidateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ValidatedIcon />
          Valider l'annonce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Êtes-vous sûr de vouloir valider cette annonce ? Elle deviendra visible aux utilisateurs.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Commentaires de validation (optionnel) :
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            value={validateReason}
            onChange={(e) => {
              setValidateReason(e.target.value);
              // Pré-remplir le champ texte libre avec la valeur sélectionnée
              setValidateReasonText(e.target.value);
              // Réinitialiser la traduction si le motif change
              setTranslatedReasons(prev => ({ ...prev, validate: '' }));
              setTranslationResults(prev => ({ ...prev, validate: null }));
              setShowTranslations(prev => ({ ...prev, validate: false }));
            }}
            sx={{ mb: 2 }}
            helperText="Sélectionnez un commentaire prédéfini ou saisissez-en un personnalisé ci-dessous"
            disabled={submitting || translating}
          >
            <MenuItem value="">
              <em>Choisir un commentaire prédéfini</em>
            </MenuItem>
            {predefinedValidateReasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>

          {/* Champ texte libre pour le commentaire de validation */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Commentaire de validation (optionnel) :
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={validateReasonText}
            onChange={(e) => {
              setValidateReasonText(e.target.value);
              // Réinitialiser la traduction si le texte change
              setTranslatedReasons(prev => ({ ...prev, validate: '' }));
              setTranslationResults(prev => ({ ...prev, validate: null }));
              setShowTranslations(prev => ({ ...prev, validate: false }));
            }}
            placeholder="Saisissez ou modifiez le commentaire de validation (optionnel)..."
            sx={{ mb: 2 }}
            helperText={
              validateReasonText.trim().length > 0 && validateReasonText.trim().length < 10
                ? 'Le commentaire doit contenir au moins 10 caractères'
                : 'Ce commentaire sera envoyé au propriétaire de l\'annonce et peut être traduit'
            }
            disabled={submitting || translating}
          />

          {/* Bouton de traduction pour la validation */}
          {validateReasonText.trim().length >= 10 && (
            <Box display="flex" alignItems="center" gap={2} mt={1} mb={2}>
              <Button
                variant="outlined"
                startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
                onClick={() => handleTranslateReason('validate', validateReasonText, validateDialog.listing?.id)}
                disabled={submitting || translating}
                size="small"
              >
                {translating ? 'Traduction...' : 'Traduire automatiquement'}
              </Button>
              {translationResults.validate && (
                <Chip 
                  label={`Vers: ${translationResults.validate.targetLanguage}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {/* Affichage de la traduction proposée pour la validation */}
          {showTranslations.validate && translatedReasons.validate && (
            <Box mb={2} p={2} sx={{ backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
              <Typography variant="subtitle2" color="success.main" gutterBottom>
                Traduction proposée en {translationResults.validate?.targetLanguage} :
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                "{translatedReasons.validate}"
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem', flex: 1, mr: 2 }}>
                  Vérifiez la traduction et cliquez sur "Utiliser cette traduction" pour l'insérer dans le champ commentaire.
                </Alert>
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  onClick={() => {
                    setValidateReasonText(translatedReasons.validate);
                    showNotification('Traduction utilisée dans le champ commentaire', 'success');
                  }}
                >
                  Utiliser cette traduction
                </Button>
              </Box>
            </Box>
          )}

          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note :</strong> La validation rendra l'annonce immédiatement visible aux utilisateurs.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeValidateDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleValidateSubmit}
            color="success"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <ValidatedIcon />}
          >
            {submitting ? 'Validation...' : (
              showTranslations.validate ? 'Confirmer la validation avec traduction' : 'Confirmer la validation'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour le rejet */}
      <Dialog
        open={rejectDialog.open}
        onClose={closeRejectDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <RejectedIcon />
          Rejeter l'annonce
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Êtes-vous sûr de vouloir rejeter cette annonce ? Elle ne sera pas publiée.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            Motif du rejet (obligatoire) :
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
              // Pré-remplir le champ texte libre avec la valeur sélectionnée
              setRejectReasonText(e.target.value);
              // Réinitialiser la traduction si le motif change
              setTranslatedReasons(prev => ({ ...prev, reject: '' }));
              setTranslationResults(prev => ({ ...prev, reject: null }));
              setShowTranslations(prev => ({ ...prev, reject: false }));
            }}
            sx={{ mb: 2 }}
            helperText="Sélectionnez un motif prédéfini ou saisissez-en un personnalisé ci-dessous"
            disabled={submitting || translating}
          >
            <MenuItem value="">
              <em>Choisir un motif prédéfini</em>
            </MenuItem>
            {predefinedRejectReasons.map((reason, index) => (
              <MenuItem key={index} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </TextField>

          {/* Champ texte libre pour le motif de rejet */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Motif du rejet (obligatoire) :
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={rejectReasonText}
            onChange={(e) => {
              setRejectReasonText(e.target.value);
              // Réinitialiser la traduction si le texte change
              setTranslatedReasons(prev => ({ ...prev, reject: '' }));
              setTranslationResults(prev => ({ ...prev, reject: null }));
              setShowTranslations(prev => ({ ...prev, reject: false }));
            }}
            placeholder="Saisissez ou modifiez le motif de rejet..."
            sx={{ mb: 2 }}
            helperText={
              rejectReasonText.trim().length > 0 && rejectReasonText.trim().length < 10
                ? 'Le motif doit contenir au moins 10 caractères'
                : 'Ce motif sera envoyé au propriétaire de l\'annonce et peut être traduit'
            }
            disabled={submitting || translating}
            error={rejectReasonText.trim().length > 0 && rejectReasonText.trim().length < 10}
            required
          />

          {/* Bouton de traduction pour le rejet */}
          <Box display="flex" alignItems="center" gap={2} mt={1} mb={2}>
            <Button
              variant="outlined"
              startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
              onClick={() => handleTranslateReason('reject', rejectReasonText, rejectDialog.listing?.id)}
              disabled={!rejectReasonText.trim() || rejectReasonText.trim().length < 10 || submitting || translating}
              size="small"
            >
              {translating ? 'Traduction...' : 'Traduire automatiquement'}
            </Button>
            {translationResults.reject && (
              <Chip 
                label={`Vers: ${translationResults.reject.targetLanguage}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Affichage de la traduction proposée pour le rejet */}
          {showTranslations.reject && translatedReasons.reject && (
            <Box mb={2} p={2} sx={{ backgroundColor: '#ffebee', borderRadius: 1, border: '1px solid #f44336' }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Traduction proposée en {translationResults.reject?.targetLanguage} :
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
                "{translatedReasons.reject}"
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Alert severity="info" sx={{ fontSize: '0.875rem', flex: 1, mr: 2 }}>
                  Vérifiez la traduction et cliquez sur "Utiliser cette traduction" pour l'insérer dans le champ motif.
                </Alert>
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={() => {
                    setRejectReasonText(translatedReasons.reject);
                    showNotification('Traduction utilisée dans le champ motif', 'success');
                  }}
                >
                  Utiliser cette traduction
                </Button>
              </Box>
            </Box>
          )}

          {/* Notes de traduction en cas d'erreur pour le rejet */}
          {translationResults.reject && translationResults.reject.error && (
            <Box mb={2}>
              <Alert severity="warning">
                {translationResults.reject.error === 'Quota épuisé' 
                  ? 'Quota de traduction épuisé. Veuillez traduire manuellement le motif dans la langue du propriétaire.' 
                  : 'Erreur de traduction. Veuillez vérifier que votre motif est en français et réessayer.'}
              </Alert>
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Attention :</strong> Le rejet empêchera la publication de cette annonce. Le propriétaire recevra une notification avec le motif.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog} color="inherit">
            Annuler
          </Button>
          <Button
            onClick={handleRejectSubmit}
            color="error"
            variant="contained"
            disabled={submitting || !rejectReasonText.trim()}
            startIcon={submitting ? <CircularProgress size={16} /> : <RejectedIcon />}
          >
            {submitting ? 'Rejet...' : 'Confirmer le rejet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AdminListingDetail;
